/**
 * F43-FC §6.3 — Teams tab panel on the admin platform-user detail page.
 *
 * Replaces the generic `TabRows` on the Teams tab. Per membership row it
 * renders, gated on `teams:moderate`:
 *   - "Remove" — remove THIS user from THAT team (disabled on the captain row,
 *     D7 "transfer captaincy or disband first"; the server re-enforces with
 *     CannotRemoveCaptainError → 409 CANNOT_REMOVE_CAPTAIN)
 *   - "Suspend team" / "Disband team" — team-wide actions (the consequence copy
 *     in the reused dialogs already says it's team-wide)
 *
 * REUSE: the team-wide actions render the SAME `SuspendTeamDialog` /
 * `DisbandTeamDialog` (props `{ open, teamName, loading, serverError, onClose,
 * onSubmit }`) and route through the SAME `adminTeamsApi.suspend` /
 * `.forceDisband` → `POST /admin/teams/:teamId/{suspend,disband}` →
 * `TeamAdminService` used by the dedicated admin team-detail page — one backing
 * service + one shared component per the founder reuse mandate.
 *
 * BUILD-NEW: the member-remove dialog (`RemoveTeamMemberDialog`) +
 * `adminTeamsApi.removeMember`.
 *
 * On success every handler refetches the USER's Teams tab (`onReload`), not the
 * team-detail page (§6.3). A server error keeps the dialog open and surfaces a
 * Banner (the `onConfirm`-re-throw / serverError pattern).
 */
import { useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Button, Card, EmptyState, Skeleton, useToast } from '@bravemove/ui-web';
import type {
  DisbandTeamByAdminInput,
  RemoveTeamMemberInput,
  SuspendTeamInput,
} from '@bravemove/types';
import { adminTeamsApi } from '../../api/teams';
import type { AdminUserTabRow } from '../../api/adminUsers';
import { translateApiError } from '../../auth/errors.js';
import { SuspendTeamDialog } from '../teams/SuspendTeamDialog';
import { DisbandTeamDialog } from '../teams/DisbandTeamDialog';
import { RemoveTeamMemberDialog } from '../teams/RemoveTeamMemberDialog';

/**
 * PU-13 — terminal MEMBERSHIP statuses: the user is no longer on the roster, so
 * "Remove" can never succeed (server → TargetMembershipNotFoundError 404).
 * Mirrors AffiliationsAdminPanel's TERMINAL set.
 */
const TERMINAL_MEMBERSHIP = new Set(['member_left', 'removed_by_captain', 'removed_by_admin']);

export function TeamsAdminPanel({
  rows,
  userId,
  userName,
  canModerate,
  onReload,
}: {
  readonly rows: readonly AdminUserTabRow[] | undefined;
  readonly userId: string;
  readonly userName: string;
  readonly canModerate: boolean;
  readonly onReload: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();

  // One active dialog at a time, keyed on (action, team row).
  const [removeTarget, setRemoveTarget] = useState<AdminUserTabRow | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<AdminUserTabRow | null>(null);
  const [disbandTarget, setDisbandTarget] = useState<AdminUserTabRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  if (rows === undefined) return <Skeleton className="h-24 w-full rounded-lg" />;
  if (!Array.isArray(rows) || rows.length === 0) {
    return <EmptyState title={t('admin:f43.empty.generic')} />;
  }

  const submitRemove = async (body: RemoveTeamMemberInput) => {
    if (!removeTarget) return;
    setSubmitting(true);
    setServerError(null);
    try {
      await adminTeamsApi.removeMember(removeTarget.id, userId, body);
      toast.push({
        tone: 'success',
        title: t('admin:f43fc.team.remove_member.done', { defaultValue: 'Member removed.' }),
      });
      setRemoveTarget(null);
      onReload();
    } catch (err) {
      setServerError(translateApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const submitSuspend = async (body: SuspendTeamInput) => {
    if (!suspendTarget) return;
    setSubmitting(true);
    setServerError(null);
    try {
      await adminTeamsApi.suspend(suspendTarget.id, body);
      toast.push({
        tone: 'success',
        title: t('admin:f43fc.team.suspend.done', { defaultValue: 'Team suspended.' }),
      });
      setSuspendTarget(null);
      onReload();
    } catch (err) {
      setServerError(translateApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const submitDisband = async (body: DisbandTeamByAdminInput) => {
    if (!disbandTarget) return;
    setSubmitting(true);
    setServerError(null);
    try {
      await adminTeamsApi.forceDisband(disbandTarget.id, body);
      toast.push({
        tone: 'success',
        title: t('admin:f43fc.team.disband.done', { defaultValue: 'Team disbanded.' }),
      });
      setDisbandTarget(null);
      onReload();
    } catch (err) {
      setServerError(translateApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card>
        <ul className="divide-y divide-outline" data-testid="f43fc-teams-panel">
          {rows.map((r) => {
            const isCaptain = r.subtitle === 'captain';
            // PU-13 — hide actions that can never succeed:
            //   - Remove only on an ACTIVE membership row;
            //   - team-wide Suspend only on an active team (re-suspending a
            //     suspended team / suspending a disbanded one server-errors);
            //   - team-wide Disband never on an already-disbanded team.
            const membershipActive = !TERMINAL_MEMBERSHIP.has(r.status ?? '');
            const teamStatus = r.teamStatus ?? 'active';
            const canSuspendTeam = teamStatus === 'active';
            const canDisbandTeam = teamStatus !== 'disbanded';
            return (
              <li
                key={r.id}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
                data-testid={`f43fc-team-row-${r.id}`}
              >
                <div className="min-w-0">
                  <p className="text-[13px] text-ink truncate">{r.title}</p>
                  {r.subtitle ? (
                    <p className="text-[12px] text-ink-3 truncate">{r.subtitle}</p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {r.status ? (
                    <Badge tone="neutral" size="sm">
                      {r.status}
                    </Badge>
                  ) : null}
                  {canModerate ? (
                    <>
                      {membershipActive ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          disabled={isCaptain}
                          title={
                            isCaptain
                              ? t('admin:f43fc.team.remove_member.captain_blocked', {
                                  defaultValue: 'Transfer captaincy or disband the team first.',
                                })
                              : undefined
                          }
                          onClick={() => {
                            setServerError(null);
                            setRemoveTarget(r);
                          }}
                          data-testid={`f43fc-team-remove-${r.id}`}
                        >
                          {t('admin:f43fc.team.remove_member.action', { defaultValue: 'Remove' })}
                        </Button>
                      ) : null}
                      {canSuspendTeam ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => {
                            setServerError(null);
                            setSuspendTarget(r);
                          }}
                          data-testid={`f43fc-team-suspend-${r.id}`}
                        >
                          {t('admin:f43fc.team.suspend.action', { defaultValue: 'Suspend team' })}
                        </Button>
                      ) : null}
                      {canDisbandTeam ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => {
                            setServerError(null);
                            setDisbandTarget(r);
                          }}
                          data-testid={`f43fc-team-disband-${r.id}`}
                        >
                          {t('admin:f43fc.team.disband.action', { defaultValue: 'Disband team' })}
                        </Button>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <RemoveTeamMemberDialog
        open={removeTarget !== null}
        teamName={removeTarget?.title ?? ''}
        memberName={userName}
        loading={submitting}
        serverError={serverError}
        onClose={() => setRemoveTarget(null)}
        onSubmit={(body) => void submitRemove(body)}
      />
      <SuspendTeamDialog
        open={suspendTarget !== null}
        teamName={suspendTarget?.title ?? ''}
        loading={submitting}
        serverError={serverError}
        onClose={() => setSuspendTarget(null)}
        onSubmit={(body) => void submitSuspend(body)}
      />
      <DisbandTeamDialog
        open={disbandTarget !== null}
        teamName={disbandTarget?.title ?? ''}
        loading={submitting}
        serverError={serverError}
        onClose={() => setDisbandTarget(null)}
        onSubmit={(body) => void submitDisband(body)}
      />
    </>
  );
}
